import { msg } from 'gt-react';
import type { CashReceiptActivity } from '@erp/rpc';
import { formatCurrency } from '@/components/common/Currency';

/**
 * One line describing a cash-receipt lifecycle entry.
 *
 * Pure and separate from the timeline component so it can be tested directly.
 * Every branch is a claim about what a past event means, and each claim is a
 * whole sentence declared with `msg()` rather than assembled from translated
 * fragments — word order and prepositions don't survive being glued together.
 * Values recorded on the activity (a customer name, a check number) travel as
 * interpolated variables, which is what they are: data, not copy.
 *
 * The `m` parameter is the decode-and-translate function from `useMessages()`.
 * It is required rather than defaulted: gt-react's provider-free `mFallback`
 * returns an encoded message's source text *without* interpolating it, so a
 * default would quietly render "Receipt arrived from {origin}" in a built
 * bundle. Callers pass the real resolver.
 *
 * One small describer per kind, keyed by a lookup, rather than one long
 * switch: each stays simple enough to read at a glance.
 */

export type ActivityPayload = Record<string, unknown> | null;

/** The decode/interpolate function `useMessages()` returns. */
export type MessageResolver = (
  message: string,
  options?: Record<string, string>,
) => string;

function readString(payload: ActivityPayload, key: string): string | null {
  const value = payload?.[key];
  return typeof value === 'string' && value.length > 0 ? value : null;
}

/**
 * Own-property lookup. These tables are keyed by strings that arrive from the
 * database, so a persisted kind of `constructor` or `toString` would otherwise
 * resolve an `Object.prototype` member and get called as a describer.
 */
function lookup<V>(table: Record<string, V>, key: string): V | undefined {
  return Object.hasOwn(table, key) ? table[key] : undefined;
}

const ORIGIN_LABELS: Record<string, string> = {
  famous: msg('Famous', {
    context:
      'cash receipt activity feed, the Famous ERP system — a product name, leave untranslated',
  }),
  plaid: msg('the bank feed', {
    context: 'cash receipt activity feed, where a receipt came from',
  }),
  email: msg('an emailed bank statement', {
    context: 'cash receipt activity feed, where a receipt came from',
  }),
  manual: msg('manual entry', {
    context: 'cash receipt activity feed, where a receipt came from',
  }),
  check_deposit: msg('a check deposit', {
    context:
      'cash receipt activity feed, where a receipt came from: a batch of scanned checks submitted together',
  }),
};

const MESSAGES = {
  arrivedFrom: msg('Receipt arrived from {origin}', {
    context: 'cash receipt activity feed, receipt ingested from a named source',
  }),
  arrived: msg('Receipt arrived', {
    context:
      'cash receipt activity feed, receipt ingested from an unknown source',
  }),
  enteredByHand: msg('Receipt entered by hand', {
    context: 'cash receipt activity feed, receipt created manually in the app',
  }),
  customerChangedFromTo: msg('Customer changed from {from} to {to}', {
    context: 'cash receipt activity feed, customer reassigned',
  }),
  customerSetTo: msg('Customer set to {to}', {
    context:
      'cash receipt activity feed, customer assigned to a receipt that had none',
  }),
  customerChanged: msg('Customer changed', {
    context: 'cash receipt activity feed, customer reassigned, names unknown',
  }),
  paymentMatched: msg('Matched to a payment', {
    context:
      'cash receipt activity feed, receipt matched to a payment automatically',
  }),
  paymentMatchedRef: msg('Matched to a payment · {reference}', {
    context:
      'cash receipt activity feed, automatic payment match with the payment reference number',
  }),
  paymentLinked: msg('Linked to a payment by hand', {
    context:
      'cash receipt activity feed, receipt linked to a payment by a person',
  }),
  paymentLinkedRef: msg('Linked to a payment by hand · {reference}', {
    context:
      'cash receipt activity feed, manual payment link with the payment reference number',
  }),
  amountDiffers: msg('{label} (amount differs)', {
    context:
      'cash receipt activity feed, qualifies a payment link whose amount does not match the receipt',
  }),
  paymentUnlinked: msg('Unlinked from its remittances', {
    context:
      'cash receipt activity feed, every remittance link on the receipt removed (older entries: its payment link)',
  }),
  remittanceLinkedAmountCustomer: msg(
    'Linked a remittance advice for {amount} from {customer}',
    { context: 'cash receipt activity feed, remittance advice attached' },
  ),
  remittanceLinkedAmount: msg('Linked a remittance advice for {amount}', {
    context:
      'cash receipt activity feed, remittance advice attached, sender unknown',
  }),
  remittanceLinkedCustomer: msg('Linked a remittance advice from {customer}', {
    context:
      'cash receipt activity feed, remittance advice attached, amount unknown',
  }),
  remittanceLinked: msg('Linked a remittance advice', {
    context: 'cash receipt activity feed, remittance advice attached',
  }),
  remittanceUnlinkedReason: msg('Unlinked a remittance advice: {reason}', {
    context:
      'cash receipt activity feed, a hand-picked remittance link was closed, with the reason the person gave',
  }),
  remittanceUnlinked: msg('Unlinked a remittance advice', {
    context:
      'cash receipt activity feed, a hand-picked remittance link was closed',
  }),
  editedFields: msg('Edited {fields}', {
    context:
      'cash receipt activity feed, lists the receipt fields an edit touched',
  }),
  editedDetails: msg('Edited receipt details', {
    context: 'cash receipt activity feed, edit that recorded no field diff',
  }),
  consolidatedFrom: msg('Folded in a duplicate receipt from {origin}', {
    context: 'cash receipt activity feed, a duplicate receipt was merged in',
  }),
  consolidated: msg('Folded in a duplicate receipt', {
    context:
      'cash receipt activity feed, a duplicate receipt of unknown origin was merged in',
  }),
  remittanceMarkedMissing: msg('Marked the remittance missing', {
    context:
      'cash receipt activity feed, a user flagged that a remittance email should have come with this cash',
  }),
  remittanceMissingCleared: msg('Cleared the missing remittance mark', {
    context: 'cash receipt activity feed, a user removed that flag',
  }),
  portalFetchRequestedFrom: msg(
    'Requested the remittance from the portal of {customer}',
    {
      context:
        "cash receipt activity feed, the remittance was requested from the customer's online portal; customer is a company name",
    },
  ),
  portalFetchRequested: msg(
    "Requested the remittance from the customer's portal",
    {
      context:
        "cash receipt activity feed, the remittance was requested from the customer's online portal, customer name unknown",
    },
  ),
};

/**
 * Where a receipt came from, in the words an AR rep uses. Falls back to the
 * raw origin rather than hiding an unrecognized one — an origin we have no
 * phrase for is still better shown than swallowed.
 */
export function describeOrigin(
  originSystem: string | null,
  m: MessageResolver,
): string | null {
  if (!originSystem) return null;
  const known = lookup(ORIGIN_LABELS, originSystem);
  return known ? m(known) : originSystem;
}

type Describer = (payload: ActivityPayload, m: MessageResolver) => string;

const DESCRIBERS: Record<string, Describer> = {
  'cash_receipt.ingested': (payload, m) => {
    const origin = describeOrigin(readString(payload, 'originSystem'), m);
    return origin ? m(MESSAGES.arrivedFrom, { origin }) : m(MESSAGES.arrived);
  },

  'cash_receipt.created': (_payload, m) => m(MESSAGES.enteredByHand),

  'cash_receipt.customer_changed': (payload, m) => {
    const to = readString(payload, 'toCustomerName');
    const from = readString(payload, 'fromCustomerName');
    if (to && from) return m(MESSAGES.customerChangedFromTo, { from, to });
    if (to) return m(MESSAGES.customerSetTo, { to });
    return m(MESSAGES.customerChanged);
  },

  'cash_receipt.payment_linked': (payload, m) => {
    const automatic = payload?.automatic === true;
    const reference = readString(payload, 'referenceNumber');
    const label = reference
      ? m(automatic ? MESSAGES.paymentMatchedRef : MESSAGES.paymentLinkedRef, {
          reference,
        })
      : m(automatic ? MESSAGES.paymentMatched : MESSAGES.paymentLinked);
    return payload?.amountMismatch === true
      ? m(MESSAGES.amountDiffers, { label })
      : label;
  },

  'cash_receipt.payment_unlinked': (_payload, m) => m(MESSAGES.paymentUnlinked),

  'cash_receipt.remittance_linked': (payload, m) => {
    const stated = readString(payload, 'statedTotalAmount');
    const customer = readString(payload, 'statedCustomerName');
    const amount = stated ? formatCurrency(stated) : null;
    if (amount && customer)
      return m(MESSAGES.remittanceLinkedAmountCustomer, { amount, customer });
    if (amount) return m(MESSAGES.remittanceLinkedAmount, { amount });
    if (customer) return m(MESSAGES.remittanceLinkedCustomer, { customer });
    return m(MESSAGES.remittanceLinked);
  },

  'cash_receipt.remittance_unlinked': (payload, m) => {
    const reason = readString(payload, 'reason');
    return reason
      ? m(MESSAGES.remittanceUnlinkedReason, { reason })
      : m(MESSAGES.remittanceUnlinked);
  },

  'cash_receipt.details_edited': (payload, m) => {
    const changed = payload?.valuesChanged;
    const fields =
      changed && typeof changed === 'object'
        ? Object.keys(changed as Record<string, unknown>)
        : [];
    return fields.length > 0
      ? m(MESSAGES.editedFields, { fields: fields.join(', ') })
      : m(MESSAGES.editedDetails);
  },

  'cash_receipt.consolidated': (payload, m) => {
    const origin = describeOrigin(readString(payload, 'originSystem'), m);
    return origin
      ? m(MESSAGES.consolidatedFrom, { origin })
      : m(MESSAGES.consolidated);
  },

  'cash_receipt.remittance_marked_missing': (_payload, m) =>
    m(MESSAGES.remittanceMarkedMissing),

  'cash_receipt.remittance_missing_cleared': (_payload, m) =>
    m(MESSAGES.remittanceMissingCleared),

  'cash_receipt.remittance_portal_fetch_requested': (payload, m) => {
    const customer = readString(payload, 'customerName');
    return customer
      ? m(MESSAGES.portalFetchRequestedFrom, { customer })
      : m(MESSAGES.portalFetchRequested);
  },
};

export function describeCashReceiptActivity(
  activity: CashReceiptActivity,
  m: MessageResolver,
): string {
  const describer = lookup(DESCRIBERS, activity.kind);
  if (describer) return describer(activity.payload, m);
  // An entry written by a newer backend than this bundle. Render the bare
  // kind rather than dropping the row — a gap in an audit trail is worse than
  // an unpolished label. Untranslated by necessity: it is a database value we
  // have no phrase for yet.
  return activity.kind.replace(/^cash_receipt\./, '').replace(/_/g, ' ');
}
